import { useState, useEffect } from 'react';
import {
  Drawer,
  Stack,
  Group,
  Text,
  TextInput,
  Textarea,
  Select,
  NumberInput,
  Button,
  Divider,
  Alert,
  Box,
  ActionIcon,
  Badge,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { AlertCircle, Plus, Trash2 } from 'react-feather';
import type {
  TaskTemplateWithDetails,
  JobTitle,
  TaskAnchorType,
  TaskPriority,
  UpdateTaskTemplateRequest,
  CreateTaskTemplateRequest,
} from '../../../../types';
import { taskTemplatesApi } from '../../../../services/api/taskTemplates';
import { TASK_ANCHOR_OPTIONS, TASK_PRIORITY_OPTIONS } from '../../../../config/taskConfig';

interface EditTemplateSidebarProps {
  template: TaskTemplateWithDetails | null;
  jobTitles: JobTitle[];
  opened: boolean;
  onClose: () => void;
  onSave: () => void;
}

interface FormData {
  task_title: string;
  task_description: string;
  job_title_id: string | null;
  anchor_type: TaskAnchorType;
  anchor_session_number: number | null;
  days_offset: number;
  default_priority: TaskPriority;
  is_required: boolean;
}

export function EditTemplateSidebar({
  template,
  jobTitles,
  opened,
  onClose,
  onSave,
}: EditTemplateSidebarProps) {
  const [formData, setFormData] = useState<FormData>({
    task_title: '',
    task_description: '',
    job_title_id: null,
    anchor_type: 'cohort_start',
    anchor_session_number: null,
    days_offset: 0,
    default_priority: 'medium',
    is_required: true,
  });
  const [subtasks, setSubtasks] = useState<TaskTemplateWithDetails[]>([]);
  const [saving, setSaving] = useState(false);
  const [loadingSubtasks, setLoadingSubtasks] = useState(false);
  const [addingSubtask, setAddingSubtask] = useState(false);
  const [newSubtaskTitle, setNewSubtaskTitle] = useState('');
  const [savingSubtask, setSavingSubtask] = useState(false);

  // Load template data when template changes
  useEffect(() => {
    if (template) {
      setFormData({
        task_title: template.task_title,
        task_description: template.task_description || '',
        job_title_id: template.job_title_id || null,
        anchor_type: template.anchor_type,
        anchor_session_number: template.anchor_session_number,
        days_offset: template.days_offset,
        default_priority: template.default_priority,
        is_required: template.is_required,
      });
      fetchSubtasks(template.id);
    }
  }, [template]);

  const fetchSubtasks = async (templateId: string) => {
    setLoadingSubtasks(true);
    try {
      const data = await taskTemplatesApi.getSubtasks(templateId);
      setSubtasks(data || []);
    } catch (error) {
      console.error('Failed to fetch subtasks:', error);
    } finally {
      setLoadingSubtasks(false);
    }
  };

  const handleSave = async () => {
    if (!template || !formData.task_title.trim()) {
      notifications.show({
        title: 'Validation Error',
        message: 'Task title is required',
        color: 'red',
      });
      return;
    }

    setSaving(true);
    try {
      const updateData: UpdateTaskTemplateRequest = {
        task_title: formData.task_title,
        task_description: formData.task_description || null,
        job_title_id: formData.job_title_id,
        anchor_type: formData.anchor_type,
        anchor_session_number: formData.anchor_session_number,
        days_offset: formData.days_offset,
        default_priority: formData.default_priority,
        is_required: formData.is_required,
      };
      await taskTemplatesApi.update(template.id, updateData);
      notifications.show({
        title: 'Success',
        message: 'Template updated successfully',
        color: 'green',
      });
      onSave();
      onClose();
    } catch (error) {
      console.error('Failed to update template:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to update template',
        color: 'red',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteSubtask = async (subtaskId: string) => {
    if (!confirm('Are you sure you want to delete this subtask?')) return;

    try {
      await taskTemplatesApi.delete(subtaskId);
      setSubtasks(prev => prev.filter(s => s.id !== subtaskId));
      notifications.show({
        title: 'Success',
        message: 'Subtask deleted',
        color: 'green',
      });
    } catch (error) {
      console.error('Failed to delete subtask:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to delete subtask',
        color: 'red',
      });
    }
  };

  const handleAddSubtask = async () => {
    if (!template || !newSubtaskTitle.trim()) {
      notifications.show({
        title: 'Validation Error',
        message: 'Subtask title is required',
        color: 'red',
      });
      return;
    }

    setSavingSubtask(true);
    try {
      const createData: CreateTaskTemplateRequest = {
        // Required fields (inherited from parent)
        cohort_type: template.cohort_type,
        milestone_name: template.milestone_name,
        task_group_name: template.task_group_name,
        task_title: newSubtaskTitle.trim(),
        // New-style fields
        template_type: template.template_type,
        phase_id: template.phase_id ?? undefined,
        sub_group_id: template.sub_group_id,
        parent_template_id: template.id,
        // Task settings
        anchor_type: template.anchor_type,
        anchor_session_number: template.anchor_session_number ?? undefined,
        days_offset: 0,
        default_priority: 'medium',
        is_required: false,
      };
      const newSubtask = await taskTemplatesApi.create(createData);
      setSubtasks(prev => [...prev, newSubtask]);
      setNewSubtaskTitle('');
      setAddingSubtask(false);
      notifications.show({
        title: 'Success',
        message: 'Subtask added',
        color: 'green',
      });
    } catch (error) {
      console.error('Failed to add subtask:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to add subtask',
        color: 'red',
      });
    } finally {
      setSavingSubtask(false);
    }
  };

  const handleCancelAddSubtask = () => {
    setAddingSubtask(false);
    setNewSubtaskTitle('');
  };

  // Format last updated date
  const formatLastUpdated = (date: string | undefined) => {
    if (!date) return 'Never';
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      title={
        <Text fw={600} size="lg">
          Edit Template
        </Text>
      }
      position="right"
      size="md"
      padding="lg"
    >
      {template && (
        <Stack gap="md">
          {/* Warning banner */}
          <Alert
            icon={<AlertCircle size={16} />}
            color="yellow"
            variant="light"
          >
            Changes only affect newly generated cohorts, not existing tasks.
          </Alert>

          {/* Per-session indicator */}
          {template.is_session_specific && (
            <Badge color="grape" variant="light" size="lg">
              Per-Session Task
            </Badge>
          )}

          {/* Task Title */}
          <TextInput
            label="Task Title"
            description={template.is_session_specific ? 'Use {session_number} for session number' : undefined}
            value={formData.task_title}
            onChange={e => setFormData(prev => ({ ...prev, task_title: e.target.value }))}
            required
          />

          {/* Description */}
          <Textarea
            label="Description"
            value={formData.task_description}
            onChange={e => setFormData(prev => ({ ...prev, task_description: e.target.value }))}
            minRows={3}
            autosize
          />

          <Divider label="Assignment" labelPosition="left" />

          {/* Default Assignee */}
          <Select
            label="Default Assignee"
            description="Job title that will be assigned this task"
            placeholder="Select job title"
            data={jobTitles.map(jt => ({ value: jt.id, label: jt.name }))}
            value={formData.job_title_id}
            onChange={value => setFormData(prev => ({ ...prev, job_title_id: value }))}
            clearable
            searchable
          />

          <Divider label="Due Date" labelPosition="left" />

          {/* Anchor Type */}
          <Select
            label="Due Date Anchor"
            description="What event determines the due date"
            data={TASK_ANCHOR_OPTIONS}
            value={formData.anchor_type}
            onChange={value =>
              setFormData(prev => ({
                ...prev,
                anchor_type: value as TaskAnchorType,
                anchor_session_number: value === 'session' ? prev.anchor_session_number : null,
              }))
            }
            required
            disabled={template.is_session_specific} // Can't change anchor for per-session tasks
          />

          {formData.anchor_type === 'session' && !template.is_session_specific && (
            <NumberInput
              label="Session Number"
              value={formData.anchor_session_number || undefined}
              onChange={value =>
                setFormData(prev => ({
                  ...prev,
                  anchor_session_number: value as number | null,
                }))
              }
              min={1}
            />
          )}

          {/* Days Offset */}
          <NumberInput
            label="Days Offset"
            description="Days before (-) or after (+) the anchor date"
            value={formData.days_offset}
            onChange={value =>
              setFormData(prev => ({ ...prev, days_offset: (value as number) || 0 }))
            }
          />

          <Divider label="Other Settings" labelPosition="left" />

          {/* Priority */}
          <Select
            label="Default Priority"
            data={TASK_PRIORITY_OPTIONS}
            value={formData.default_priority}
            onChange={value =>
              setFormData(prev => ({ ...prev, default_priority: value as TaskPriority }))
            }
            required
          />

          {/* Required */}
          <Select
            label="Required"
            description="Whether this task must be completed"
            data={[
              { value: 'true', label: 'Yes - Required' },
              { value: 'false', label: 'No - Optional' },
            ]}
            value={formData.is_required ? 'true' : 'false'}
            onChange={value => setFormData(prev => ({ ...prev, is_required: value === 'true' }))}
          />

          {/* Subtasks Section */}
          {!template.parent_template_id && (
            <>
              <Divider label="Subtasks" labelPosition="left" />

              <Box>
                {loadingSubtasks ? (
                  <Text size="sm" c="dimmed">
                    Loading subtasks...
                  </Text>
                ) : subtasks.length === 0 ? (
                  <Text size="sm" c="dimmed">
                    No subtasks
                  </Text>
                ) : (
                  <Stack gap="xs">
                    {subtasks.map(subtask => (
                      <Group key={subtask.id} justify="space-between" wrap="nowrap">
                        <Text size="sm" style={{ flex: 1 }} lineClamp={1}>
                          {subtask.task_title}
                        </Text>
                        <ActionIcon
                          variant="subtle"
                          color="red"
                          size="sm"
                          onClick={() => handleDeleteSubtask(subtask.id)}
                        >
                          <Trash2 size={14} />
                        </ActionIcon>
                      </Group>
                    ))}
                  </Stack>
                )}

                {addingSubtask ? (
                  <Stack gap="xs" mt="sm">
                    <TextInput
                      placeholder="Subtask title..."
                      value={newSubtaskTitle}
                      onChange={e => setNewSubtaskTitle(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === 'Enter' && newSubtaskTitle.trim()) {
                          handleAddSubtask();
                        } else if (e.key === 'Escape') {
                          handleCancelAddSubtask();
                        }
                      }}
                      autoFocus
                      size="xs"
                    />
                    <Group gap="xs">
                      <Button
                        variant="default"
                        size="xs"
                        onClick={handleCancelAddSubtask}
                      >
                        Cancel
                      </Button>
                      <Button
                        size="xs"
                        onClick={handleAddSubtask}
                        loading={savingSubtask}
                        disabled={!newSubtaskTitle.trim()}
                      >
                        Add
                      </Button>
                    </Group>
                  </Stack>
                ) : (
                  <Button
                    variant="light"
                    size="xs"
                    leftSection={<Plus size={14} />}
                    mt="sm"
                    onClick={() => setAddingSubtask(true)}
                  >
                    Add Subtask
                  </Button>
                )}
              </Box>
            </>
          )}

          {/* Last updated */}
          <Text size="xs" c="dimmed" ta="right" mt="md">
            Last updated: {formatLastUpdated(template.updated_at)}
          </Text>

          {/* Actions */}
          <Group justify="flex-end" mt="lg">
            <Button variant="subtle" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={handleSave} loading={saving}>
              Save Changes
            </Button>
          </Group>
        </Stack>
      )}
    </Drawer>
  );
}

export default EditTemplateSidebar;
